import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/apiConfig';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, Users, Car, CreditCard, Clock, MapPin,
  Star, Shield, Activity, RefreshCw
} from 'lucide-react';

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe',
                '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#ec4899', '#14b8a6'];

const STATUS_COLORS = {
  available: '#10b981', matched: '#3b82f6', started: '#f59e0b',
  completed: '#6366f1', cancelled: '#ef4444', expired: '#94a3b8',
  pending: '#f59e0b', confirmed: '#3b82f6', pending_lock: '#a78bfa',
};

const cardStyle = {
  background: 'rgba(15, 23, 42, 0.92)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: '16px',
  padding: '1.5rem',
  color: '#e2e8f0',
};

const headingStyle = {
  fontSize: '0.85rem', fontWeight: 600, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem',
  display: 'flex', alignItems: 'center', gap: '0.5rem',
};

const statBadge = (value, label, color = '#6366f1') => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '1.75rem', fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>{label}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#e2e8f0',
    }}>
      <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span>{p.name}:</span>
          <span style={{ fontWeight: 600 }}>{typeof p.value === 'number' ? p.value.toLocaleString('de-DE') : p.value}</span>
        </div>
      ))}
    </div>
  );
};

export const AdminAnalyticsTab = ({ stats, revenue }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics/overview?days=${period}`, { headers: getHeaders() });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Analytics load error:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  if (loading || !analytics) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem', color: '#94a3b8' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '0.75rem' }}>Loading analytics...</span>
      </div>
    );
  }

  const { daily_signups, daily_rides, daily_bookings, daily_revenue,
          ride_statuses, booking_statuses, top_routes, peak_hours,
          user_growth, rating_distribution, redis } = analytics;

  // Merge daily data for the activity chart
  const allDays = new Set([
    ...daily_signups.map(d => d.day),
    ...daily_rides.map(d => d.day),
    ...daily_bookings.map(d => d.day),
  ]);
  const activityData = [...allDays].sort().map(day => ({
    day: day.slice(5), // MM-DD
    signups: daily_signups.find(d => d.day === day)?.count || 0,
    rides: daily_rides.find(d => d.day === day)?.count || 0,
    bookings: daily_bookings.find(d => d.day === day)?.count || 0,
  }));

  const revenueData = daily_revenue.map(d => ({
    day: d.day.slice(5),
    revenue: Math.round(d.revenue * 100) / 100,
    profit: Math.round(d.profit * 100) / 100,
  }));

  const growthData = user_growth.map(d => ({ day: d.day.slice(5), users: d.total }));

  const peakData = Array.from({ length: 24 }, (_, h) => {
    const found = peak_hours.find(p => p.hour === h);
    return { hour: `${String(h).padStart(2, '0')}:00`, rides: found?.count || 0 };
  });

  return (
    <div data-testid="admin-analytics-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ── Period Selector + KPI Strip ── */}
      <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setPeriod(d)} data-testid={`period-${d}d`}
              style={{
                padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                background: period === d ? '#6366f1' : 'rgba(255,255,255,0.08)',
                color: period === d ? '#fff' : '#94a3b8', fontWeight: 600, fontSize: '0.85rem',
                transition: 'all 0.2s',
              }}>
              {d}d
            </button>
          ))}
          <button onClick={loadAnalytics} style={{
            padding: '0.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
            background: 'rgba(255,255,255,0.08)', color: '#94a3b8', display: 'flex', alignItems: 'center',
          }}><RefreshCw size={16} /></button>
        </div>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          {statBadge(stats?.total_users || 0, 'Users', '#6366f1')}
          {statBadge(stats?.total_rides || 0, 'Rides', '#3b82f6')}
          {statBadge(stats?.total_bookings || 0, 'Bookings', '#10b981')}
          {statBadge(`${(revenue?.total_volume || 0).toFixed(0)}€`, 'Volume', '#f59e0b')}
        </div>
      </div>

      {/* ── Row 1: Activity + Revenue ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <div style={cardStyle}>
          <div style={headingStyle}><Activity size={16} /> Daily Activity</div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={activityData}>
              <defs>
                <linearGradient id="gSignups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.7}/>
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.15}/>
                </linearGradient>
                <linearGradient id="gRides" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.7}/>
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.15}/>
                </linearGradient>
                <linearGradient id="gBookings" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.7}/>
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.15}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '0.75rem', color: '#94a3b8' }} />
              <Area type="monotone" dataKey="signups" stroke="#6366f1" fill="url(#gSignups)" strokeWidth={2.5} name="Signups" />
              <Area type="monotone" dataKey="rides" stroke="#3b82f6" fill="url(#gRides)" strokeWidth={2.5} name="Rides" />
              <Area type="monotone" dataKey="bookings" stroke="#10b981" fill="url(#gBookings)" strokeWidth={2.5} name="Bookings" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={cardStyle}>
          <div style={headingStyle}><CreditCard size={16} /> Revenue</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} unit="€" />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '0.75rem', color: '#94a3b8' }} />
              <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} name="Revenue" />
              <Bar dataKey="profit" fill="#10b981" radius={[4, 4, 0, 0]} name="Profit" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Row 2: User Growth + Peak Hours ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <div style={cardStyle}>
          <div style={headingStyle}><Users size={16} /> User Growth</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={growthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="users" stroke="#a78bfa" strokeWidth={2.5} dot={false} name="Total Users" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={cardStyle}>
          <div style={headingStyle}><Clock size={16} /> Peak Departure Hours</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={peakData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="hour" stroke="#64748b" fontSize={10} tickLine={false} interval={2} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="rides" name="Rides" radius={[3, 3, 0, 0]}>
                {peakData.map((_, i) => (
                  <Cell key={i} fill={i >= 6 && i <= 9 ? '#f59e0b' : i >= 16 && i <= 19 ? '#ef4444' : '#6366f1'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Row 3: Ride Statuses + Booking Statuses ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <div style={cardStyle}>
          <div style={headingStyle}><Car size={16} /> Ride Status Distribution</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <ResponsiveContainer width="50%" height={200}>
              <PieChart>
                <Pie data={ride_statuses} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                  {ride_statuses.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: 1 }}>
              {ride_statuses.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[s.status] || COLORS[i], flexShrink: 0 }} />
                  <span style={{ color: '#94a3b8', flex: 1 }}>{s.status}</span>
                  <span style={{ fontWeight: 700, color: '#e2e8f0' }}>{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={headingStyle}><CreditCard size={16} /> Booking Status Distribution</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <ResponsiveContainer width="50%" height={200}>
              <PieChart>
                <Pie data={booking_statuses} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                  {booking_statuses.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: 1 }}>
              {booking_statuses.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[s.status] || COLORS[i], flexShrink: 0 }} />
                  <span style={{ color: '#94a3b8', flex: 1 }}>{s.status}</span>
                  <span style={{ fontWeight: 700, color: '#e2e8f0' }}>{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 4: Top Routes + Ratings + Redis ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
        <div style={cardStyle}>
          <div style={headingStyle}><MapPin size={16} /> Top Routes</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {top_routes.length === 0 && <div style={{ color: '#64748b', fontSize: '0.85rem' }}>No routes yet</div>}
            {top_routes.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem' }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: i < 3 ? '#6366f1' : 'rgba(255,255,255,0.08)', color: i < 3 ? '#fff' : '#94a3b8',
                  fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                }}>{i + 1}</span>
                <span style={{ color: '#e2e8f0', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.from} → {r.to}
                </span>
                <span style={{ fontWeight: 700, color: '#a78bfa' }}>{r.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={headingStyle}><Star size={16} /> Rating Distribution</div>
          {rating_distribution.length === 0
            ? <div style={{ color: '#64748b', fontSize: '0.85rem' }}>No ratings yet</div>
            : <ResponsiveContainer width="100%" height={180}>
                <BarChart data={rating_distribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="stars" stroke="#64748b" fontSize={12} tickLine={false}
                    tickFormatter={(v) => `${'★'.repeat(v)}`} width={70} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Ratings" radius={[0, 4, 4, 0]}>
                    {rating_distribution.map((_, i) => (
                      <Cell key={i} fill={['#ef4444', '#f59e0b', '#eab308', '#84cc16', '#10b981'][i] || '#6366f1'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
          }
        </div>

        <div style={cardStyle}>
          <div style={headingStyle}><Shield size={16} /> Redis Infrastructure</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.82rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8' }}>Status</span>
              <span style={{
                padding: '0.2rem 0.6rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem',
                background: redis?.health?.status === 'healthy' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                color: redis?.health?.status === 'healthy' ? '#10b981' : '#ef4444',
              }}>{redis?.health?.status || 'unknown'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8' }}>Memory</span>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{redis?.health?.used_memory_human || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8' }}>Tracked Rides</span>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{redis?.seats?.tracked_rides || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8' }}>Active Locks</span>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{redis?.seats?.active_locks || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8' }}>Matcher Cache</span>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{redis?.matcher?.cached_subscriptions || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8' }}>Rate Limited</span>
              <span style={{ color: redis?.rate_limiter?.total_rejected > 0 ? '#f59e0b' : '#e2e8f0', fontWeight: 600 }}>
                {redis?.rate_limiter?.total_rejected || 0} / {redis?.rate_limiter?.total_requests || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Responsive: stack on mobile ── */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          [data-testid="admin-analytics-tab"] > div:nth-child(n+2) {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 600px) {
          [data-testid="admin-analytics-tab"] > div:first-child {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
};
